import { useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useDream } from '@/hooks/useDream'
import { useGoal } from '@/hooks/useGoal'
import { usePlan } from '@/hooks/usePlan'
import { useItem } from '@/hooks/useItem'
import { useProUpgrade } from '@/hooks/useProUpgrade'
import { useLanguage } from '@/hooks/useLanguage'
import { useToastContext } from '@/store/contexts/ToastContext'
import { useConfirmContext } from '@/store/contexts/ConfirmContext'
import { useLoading } from '@/store/contexts/LoadingContext'
import { t } from '@/utils/i18n'
import { DreamityGoalResDto } from '@/store/types/goal.types'
import { DreamityPlanResDto } from '@/store/types/plan.types'
import Button from '@/components/common/Button/Button'
import Modal from '@/components/common/Modal/Modal'
import { Input } from '@/components/common/Input/Input'
import Skeleton from '@/components/common/Skeleton/Skeleton'
import EmptyState from '@/components/common/EmptyState/EmptyState'
import PageHeader from '@/components/common/PageHeader/PageHeader'
import PlanCreateModal from '@/components/planner/PlanCreateModal/PlanCreateModal'
import PlanEditModal from '@/components/planner/PlanEditModal/PlanEditModal'
import Checklist from '@/pages/Checklist'
import PlanCard from '@/components/planner/PlanCard/PlanCard'
import { IconTarget, IconChevronDown, IconChevronRight, IconEdit, IconTrash, IconPlus } from '@/components/icons/Icons'
import './GoalList.css'

function GoalList() {
  const { id } = useParams<{ id: string }>()
  const { language } = useLanguage()
  const { showToast } = useToastContext()
  const { confirm } = useConfirmContext()
  const { startLoading, stopLoading } = useLoading()
  const { fetchDreamById } = useDream()
  const { fetchGoals, goals, loading: goalLoading, createGoal, updateGoal, deleteGoal } = useGoal()
  const { plansMap, fetchPlans, updatePlan, deletePlan } = usePlan()
  const { itemsMap, fetchItems } = useItem()
  const { handleRestrictionError } = useProUpgrade()

  const dreamId = id ?? null

  // UI States
  const [expandedGoals, setExpandedGoals] = useState<Set<string>>(new Set())
  const [goalName, setGoalName] = useState('')
  const [selectedGoal, setSelectedGoal] = useState<DreamityGoalResDto | null>(null)
  const [selectedPlan, setSelectedPlan] = useState<DreamityPlanResDto | null>(null)
  const [isChecklistModalOpen, setIsChecklistModalOpen] = useState(false)
  const [checklistPlanId, setChecklistPlanId] = useState<string | null>(null)
  const [checklistGoalId, setChecklistGoalId] = useState<string | null>(null)
  
  // Modal States
  const [isCreateGoalModalOpen, setIsCreateGoalModalOpen] = useState(false)
  const [isEditGoalModalOpen, setIsEditGoalModalOpen] = useState(false)
  const [isCreatePlanModalOpen, setIsCreatePlanModalOpen] = useState(false)
  const [isEditPlanModalOpen, setIsEditPlanModalOpen] = useState(false)

  // Initial Fetch
  useEffect(() => {
    if (dreamId) {
      fetchGoals(dreamId).catch(() => {})
      fetchDreamById(dreamId).catch(() => {})
    }
  }, [dreamId, fetchGoals, fetchDreamById])

  // 자동 확장: 목표가 로드되면 처음 한 번만 모든 목표를 펼친 상태로 초기화
  useEffect(() => {
    if (goals.length > 0 && expandedGoals.size === 0) {
      const allGoalIds = goals.map(g => g.goalId)
      setExpandedGoals(new Set(allGoalIds))
    }
  }, [goals]) // eslint-disable-next-line react-hooks/exhaustive-deps

  // 목표별 계획 데이터 로드 (확장 시에만)
  useEffect(() => {
    if (!dreamId) return
    expandedGoals.forEach(goalId => {
      if (!plansMap[goalId]) {
        fetchPlans(dreamId, goalId).catch(() => {})
      }
    })
  }, [dreamId, expandedGoals, plansMap, fetchPlans])

  // 계획별 아이템 로드 (예산 합산용) - 이미 로드된 경우 건너뜀
  useEffect(() => {
    if (!dreamId) return
    const allCurrentPlans = Object.values(plansMap).flat()
    allCurrentPlans.forEach(plan => {
      if (!itemsMap[plan.planId]) {
        fetchItems(dreamId, plan.planId).catch(() => {})
      }
    })
  }, [dreamId, plansMap, itemsMap, fetchItems])

  // Handlers
  const handleGoalToggle = (goalId: string) => {
    setExpandedGoals(prev => {
      const next = new Set(prev)
      if (next.has(goalId)) next.delete(goalId)
      else next.add(goalId)
      return next
    })
  }

  const handleCreateGoal = async () => {
    if (!dreamId || !goalName.trim()) return
    try {
      startLoading()
      await createGoal({ dreamId, goalName: goalName.trim() })
      setIsCreateGoalModalOpen(false)
      setGoalName('')
      showToast(t('messages.createSuccess', language), 'success')
    } catch (error: any) {
      const handled = await handleRestrictionError(error)
      if (!handled) showToast(t('messages.createFailed', language), 'error')
    } finally {
      stopLoading()
    }
  }

  const handleUpdateGoal = async () => {
    if (!selectedGoal || !goalName.trim()) return
    try {
      startLoading()
      await updateGoal(selectedGoal.goalId, { goalName: goalName.trim() })
      setIsEditGoalModalOpen(false)
      setSelectedGoal(null)
      setGoalName('')
      showToast(t('messages.updateSuccess', language), 'success')
    } catch (error) {
      showToast(t('messages.updateFailed', language), 'error')
    } finally {
      stopLoading()
    }
  }

  const handleDeleteGoal = async (goal: DreamityGoalResDto, e: React.MouseEvent) => {
    e.stopPropagation()
    const confirmed = await confirm({ message: t('goals.deleteGoalConfirm', language), variant: 'danger' })
    if (confirmed) {
      try {
        startLoading()
        await deleteGoal(goal.goalId)
        showToast(t('messages.deleteSuccess', language), 'success')
      } catch (error) {
        showToast(t('messages.deleteFailed', language), 'error')
      } finally {
        stopLoading()
      }
    }
  }

  const handlePlanClick = (planId: string, goalId: string) => {
    setChecklistPlanId(planId)
    setChecklistGoalId(goalId)
    setIsChecklistModalOpen(true)
  }

  const handleDeletePlan = async (planId: string) => {
    try {
      startLoading()
      await deletePlan(dreamId!, planId)
      showToast(t('messages.deleteSuccess', language), 'success')
    } catch {
      showToast(t('messages.deleteFailed', language), 'error')
    } finally {
      stopLoading()
    }
  }

  const handleTogglePlanComplete = async (plan: DreamityPlanResDto) => {
    if (!plan.planId) return
    try {
      startLoading()
      await updatePlan(dreamId!, plan.planId, {
        planName: plan.planName,
        planDesc: plan.planDesc,
        frequency: plan.frequency,
        frequencyDay: plan.frequencyDay,
        isCompleted: !plan.isCompleted,
        startDate: plan.startDate,
        endDate: plan.endDate,
      })
      await fetchPlans(dreamId!, plan.goalId!)
      await fetchDreamById(dreamId!) // 상위 꿈 정보(진행률 등) 갱신
      showToast(t('messages.updateSuccess', language), 'success')
    } catch (error) {
      showToast(t('messages.updateFailed', language), 'error')
    } finally {
      stopLoading()
    }
  }

  if (goalLoading && goals.length === 0) {
    return (
      <div className="goal-list p-4">
        <Skeleton height={200} className="mb-4" />
        <Skeleton height={200} className="mb-4" />
        <Skeleton height={200} />
      </div>
    )
  }

  return (
    <div className="goal-list">
      <PageHeader showBack={false} />
      
      <div className="goal-list__content">
        {goals.length === 0 ? (
          <EmptyState icon={<IconTarget size={32} />} title={t('goals.noGoals', language)} />
        ) : (
          <div className="goal-list__accordion">
            {goals.map(goal => {
              const isExpanded = expandedGoals.has(goal.goalId)
              const plans = plansMap[goal.goalId] || []
              
              return (
                <div key={goal.goalId} className="goal-list__accordion-item">
                  <div className="goal-list__accordion-header" onClick={() => handleGoalToggle(goal.goalId)}>
                    <div className="goal-list__accordion-title">
                      <span className="goal-list__accordion-icon">
                        {isExpanded ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}
                      </span>
                      <h3>{goal.goalName}</h3>
                    </div>
                    <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" onClick={() => { setSelectedGoal(goal); setGoalName(goal.goalName); setIsEditGoalModalOpen(true); }}>
                        <IconEdit size={18} />
                      </Button>
                      <Button variant="danger" size="sm" onClick={e => handleDeleteGoal(goal, e)}>
                        <IconTrash size={18} />
                      </Button>
                      <Button variant="primary" size="sm" onClick={() => { setSelectedGoal(goal); setIsCreatePlanModalOpen(true); }}>
                        <IconPlus size={18} />
                      </Button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="goal-list__accordion-content">
                      {plans.length === 0 ? (
                        <div className="p-4 text-center text-gray-500 italic">{t('goalDetail.noPlans', language)}</div>
                      ) : (
                        <div className="goal-list__plans">
                          {plans.map(plan => {
                            const planItems = itemsMap[plan.planId] || []
                            const planBudget = planItems.reduce((sum, item) => sum + (item.itemPrice || 0), 0)
                            const progressRate = planItems.length > 0 
                              ? planItems.filter(i => i.isCompleted).length / planItems.length 
                              : (plan.isCompleted ? 1 : 0)
                            
                            return (
                              <PlanCard
                                key={plan.planId}
                                plan={{
                                  ...plan,
                                  totalItemPrice: planBudget
                                }}
                                progressRate={progressRate}
                                onClick={() => handlePlanClick(plan.planId, goal.goalId)}
                                onEdit={() => { setSelectedPlan(plan); setIsEditPlanModalOpen(true); }}
                                onDelete={handleDeletePlan}
                                onToggleComplete={handleTogglePlanComplete}
                              />
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      <button className="fab" onClick={() => setIsCreateGoalModalOpen(true)}>
        <IconPlus size={24} />
      </button>

      {/* Modals */}
      <Modal isOpen={isCreateGoalModalOpen} onClose={() => setIsCreateGoalModalOpen(false)} title={t('goals.createButton', language)} size="sm">
        <div className="p-4 flex flex-col gap-4">
          <Input label={t('goals.goalName', language)} value={goalName} onChange={e => setGoalName(e.target.value)} required />
          <Button onClick={handleCreateGoal}>{t('common.create', language)}</Button>
        </div>
      </Modal>

      <Modal isOpen={isEditGoalModalOpen} onClose={() => setIsEditGoalModalOpen(false)} title={t('goals.editGoalTitle', language)} size="sm">
        <div className="p-4 flex flex-col gap-4">
          <Input label={t('goals.goalName', language)} value={goalName} onChange={e => setGoalName(e.target.value)} required />
          <Button onClick={handleUpdateGoal}>{t('common.save', language)}</Button>
        </div>
      </Modal>

      <PlanCreateModal
        isOpen={isCreatePlanModalOpen}
        onClose={() => setIsCreatePlanModalOpen(false)}
        initialDreamId={dreamId}
        initialGoalId={selectedGoal?.goalId}
        onCreated={() => setIsCreatePlanModalOpen(false)}
      />

      <PlanEditModal
        isOpen={isEditPlanModalOpen}
        onClose={() => setIsEditPlanModalOpen(false)}
        plan={selectedPlan}
        dreamId={dreamId}
      />

      {isChecklistModalOpen && (
        <Modal isOpen={isChecklistModalOpen} onClose={() => setIsChecklistModalOpen(false)}>
          <Checklist dreamIdProp={dreamId!} goalIdProp={checklistGoalId!} planIdProp={checklistPlanId!} isModal onClose={() => setIsChecklistModalOpen(false)} />
        </Modal>
      )}
    </div>
  )
}

export default GoalList
